import React from "react";
import InputBox from "../Components/InputBox";
import { MdSave, MdCancel, MdModeEdit } from "react-icons/md";
import {
  FaUser,
  FaBuilding,
  FaHome,
  FaPhone,
  FaMapMarked,
} from "react-icons/fa";
import CompanyTerms from "../../../components/CompanyTerms";

const InfoCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-lg border border-gray-100 p-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-gray-400" />
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const InfoItem = ({
  label,
  value,
  editValue,
  name,
  type = "text",
  handleChange,
}) => (
  <div className="flex items-start justify-between group">
    <span className="text-xs text-gray-500">{label}</span>
    {editValue !== undefined ? (
      <InputBox
        value={editValue}
        onChange={handleChange}
        name={name}
        type={type}
        className="w-48 text-right bg-blue-50 border-blue-200 focus:border-eleghoBlue focus:ring-1 focus:ring-eleghoBlue transition-colors duration-200 px-3 py-1.5"
      />
    ) : (
      <span className="text-sm font-medium text-gray-900">{value}</span>
    )}
  </div>
);

export default function ResidentForm({
  resident,
  formData,
  editMode,
  isProcessing,
  handleChange,
  handleEditMode,
  handleSave,
  allowableAccess,
  userRoles,
  terms,
  companyData,
}) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {resident.identifier}
          </h2>
          <p className="text-sm text-gray-500">Resident Information</p>
        </div>
        {allowableAccess.some((role) => userRoles.includes(role)) && (
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button
                  onClick={handleEditMode}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
                >
                  <MdCancel className="w-4 h-4 mr-1.5" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-eleghoBlue rounded-md hover:bg-eleghoBlue/90 transition-colors duration-200"
                >
                  <MdSave className="w-4 h-4 mr-1.5" />
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={handleEditMode}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-eleghoBlue bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
              >
                <MdModeEdit className="w-4 h-4 mr-1.5" />
                Edit Information
              </button>
            )}
          </div>
        )}
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Information */}
        <InfoCard title="Basic Information" icon={FaUser}>
          <InfoItem
            label="Primary Owner"
            value={resident.owner1}
            editValue={editMode ? formData.owner1 : undefined}
            name="owner1"
            handleChange={handleChange}
          />
          <InfoItem
            label="Secondary Owner"
            value={resident.owner2}
            editValue={editMode ? formData.owner2 : undefined}
            name="owner2"
            handleChange={handleChange}
          />
          <InfoItem
            label="Nationality"
            value={resident.nationality}
            editValue={editMode ? formData.nationality : undefined}
            name="nationality"
            handleChange={handleChange}
          />
          <InfoItem
            label="Address"
            value={resident.address}
            editValue={editMode ? formData.address : undefined}
            name="address"
            handleChange={handleChange}
          />
        </InfoCard>

        {/* Contact Information */}
        <InfoCard title="Contact Information" icon={FaPhone}>
          <InfoItem
            label="Email"
            value={resident.email}
            editValue={editMode ? formData.email : undefined}
            name="email"
            handleChange={handleChange}
          />
          <InfoItem
            label="Phone Number"
            value={resident.phoneNumber}
            editValue={editMode ? formData.phoneNumber : undefined}
            name="phoneNumber"
            handleChange={handleChange}
          />
        </InfoCard>

        {/* Unit Information */}
        <InfoCard
          title={
            <>
              <CompanyTerms term="unit" capitalize={true} /> Information
            </>
          }
          icon={FaBuilding}
        >
          <InfoItem
            label={
              <>
                <CompanyTerms term="structure" capitalize={true} /> Number
              </>
            }
            value={resident.phase}
            editValue={editMode ? formData.phase : undefined}
            name="phase"
            handleChange={handleChange}
          />
          <InfoItem
            label={
              <>
                <CompanyTerms term="location" capitalize={true} /> Number
              </>
            }
            value={resident.block}
            editValue={editMode ? formData.block : undefined}
            name="block"
            handleChange={handleChange}
          />
          <InfoItem
            label={<CompanyTerms term="identifier" capitalize={true} />}
            value={resident.lot}
            editValue={editMode ? formData.lot : undefined}
            name="lot"
            handleChange={handleChange}
          />
          {companyData && companyData.entityType === "hoa" && (
            <InfoItem
              label="Street Name"
              value={resident.streetName || ""}
              editValue={editMode ? formData.streetName : undefined}
              name="streetName"
              handleChange={handleChange}
            />
          )}
          <InfoItem
            label="Property Type"
            value={resident.unitType}
            editValue={editMode ? formData.unitType : undefined}
            name="unitType"
            handleChange={handleChange}
          />
        </InfoCard>

        {/* Additional Details */}
        <InfoCard title="Additional Details" icon={FaHome}>
          <InfoItem
            label={
              <>
                <CompanyTerms term="unit" capitalize={true} /> Size
              </>
            }
            value={`${resident.lotSize} sqm`}
            editValue={editMode ? formData.lotSize : undefined}
            name="lotSize"
            handleChange={handleChange}
          />
          <InfoItem
            label="Number of Occupants"
            value={resident.numberOfOccupants}
            editValue={editMode ? formData.numberOfOccupants : undefined}
            name="numberOfOccupants"
            type="number"
            handleChange={handleChange}
          />
        </InfoCard>
      </div>
    </div>
  );
}
